<script lang='ts'>

    /* IMPORTS --------------------------------------------------------------------------------- */

    import { Button, Icon, integer } from '@smatch/cassiopeia-web-components';

    // COMPONENTS
    import ProductListItem from './ProductListItem.svelte';
    import ProductListHeader from './ProductListHeader.svelte';
    import Title from 'src/app/library/title/Title.svelte';

    /* TYPES ----------------------------------------------------------------------------------- */

    import type { Auction, Products } from 'src/app/types';

    /* EXPORTS --------------------------------------------------------------------------------- */

    export let auction: Auction;
    export let products: Products | undefined = undefined;
    export let isPreview: boolean = false;
    export let isLoading: boolean = false;
    export let isEndOfList: boolean = false;

    /* INTERNALS ------------------------------------------------------------------------------- */

    const CONFIGURABLE_COLUMNS: Record<string, boolean> = {
        hasBrand: true,
        hasCategory: true,
        hasColor: false,
        hasGender: false,
        hasSeason: false,
    };

    const FIXED_COLUMNS: string[] = [
        'name',
        'total_count',
        'retail_price',
        'offer_price',
        'discount',
        'distribution',
    ];

    const getColumns = (sizeColumnsCount: number): string => {

        const CONFIGURABLE_COLUMN_COUNT = Object.values(CONFIGURABLE_COLUMNS).filter(Boolean).length;
        const FIXED_COLUMN_COUNT = FIXED_COLUMNS.length;

        return `grid-template-columns: repeat(${ CONFIGURABLE_COLUMN_COUNT + FIXED_COLUMN_COUNT }, auto) `
            + `[dimensions-start] repeat(${ sizeColumnsCount }, auto) [dimensions-end];`;
    };

    const hasProductColor = (): boolean => {

        if (products === undefined) {
            return false;
        }

        // Disabling the label check due to a bad product import
        // eslint-disable-next-line max-len, @typescript-eslint/no-unnecessary-condition
        return products.some(product => product.attributes?.some(attr => attr.type === 'color' && attr.label !== undefined));
    };

    const hasProductGender = (): boolean => {
        if (products === undefined) {
            return false;
        };

        return products.some(product => Boolean(product.genderCode));
    };

    const hasProductSeason = (): boolean => {
        if (products === undefined) {
            return false;
        };

        return products.some(product => Boolean(product.season));
    };

    CONFIGURABLE_COLUMNS.hasColor = hasProductColor();
    CONFIGURABLE_COLUMNS.hasGender = hasProductGender();
    CONFIGURABLE_COLUMNS.hasSeason = hasProductSeason();

    const getMaxSizeRunLength = (products: Products | undefined): number => {

        if (!Array.isArray(products) || products.length === 0) {
            return 0;
        }

        const SIZE_RUNS = products.filter(product => Array.isArray(product.sizes))
            .map(product => product.sizes)
            .sort((runA, runB) => (runB?.length ?? 0) - (runA?.length ?? 0));

        if (SIZE_RUNS.length === 0) {
            return 0;
        }

        return SIZE_RUNS[0]!.length;
    };


    $: maxColumns = getMaxSizeRunLength(products);

</script>

<Title title = {`Deal ${ auction.name } (${ auction._.key })`} />

{#if Array.isArray(products) && products.length > 0}

    <main id = 's8-product-list' class:s8-preview = { isPreview }>

        <div id = 's8-grid' style = { getColumns(maxColumns) }>

            <ProductListHeader config = { CONFIGURABLE_COLUMNS } { auction }/>

            {#each products as product, index}
                <ProductListItem
                    { product }
                    { index }
                    config = { CONFIGURABLE_COLUMNS }
                    currencyCode = { auction.locale.currencyCode }
                    maxSizes = { maxColumns }
                />
            {/each}

        </div>

        {#if isPreview && auction.total.quantity.products > 20 }
            <div class = 's8-preview-hint'>
                Want to see the remaining { integer(auction.total.quantity.products - 20, false) } products?
                <Button
                    url = '#authentication'
                    label = 'Register or Login'
                    size = 'xs'
                    color = 'dark'
                    css = 'mx-1 mt-1'
                />
                to see the full deal.
            </div>
        {/if}

        {#if !isPreview && isLoading}
            <div class = 's8-loading'>
                <Icon key = 'loading' />
            </div>
        {/if}

        {#if !isPreview && isEndOfList}
            <div class = 's8-end-of-list'>End of the list.</div>
        {/if}

    </main>

{/if}

<style lang='postcss'>
    main#s8-product-list {
        @apply w-full overflow-y-scroll pb-[55px] sm:pb-[79px] mt-16 md:mt-12;

        &.s8-preview {
            @apply mt-52 xxs:mt-48 xs:mt-44 sm:mt-40 md:mt-32 lg:mt-28;
        }

        div#s8-grid {
            @apply border-t border-grey-200 auto-rows-min grid text-xs overflow-y-scroll;
        }
    }

    div.s8-preview-hint {
        @apply bg-primary-400 p-4 text-center text-light;
    }

    div.s8-loading {
        justify-self: center;
        padding: 10px;
        transform-origin: center;
        animation: spinner 0.75s infinite linear;
        @keyframes spinner {
            100% {
                transform: rotate(360deg);
            }
        }
    }

    div.s8-end-of-list {
        justify-self: center;
        padding: 10px;
    }

</style>